/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import Tbl from '@appblocks/codegeneration/Libraries/Tbl';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import Cgg from '.';


export const getCodeBlocks = (feature: Cgg): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project: ProjectConfiguration = feature.project;
    if (!feature.getEnabled()) {
        return blocks;
    }

    const INTERFACE = 'current_interface';
    const TABLES = feature.getSettingValue('TABLES');
    const SERVER = feature.getSettingValue('SERVER');
    let SERVER_PORT = 1883;
    const settings = [];
    const CGG_MAX_SETTING_NAME_LENGTH = 0;
    const USERNAME = feature.getSettingValue('USERNAME');
    const PASSWORD = feature.getSettingValue('PASSWORD');
    const GROUPS = feature.getSettingValue('GROUPS');
    const DATA_TABLES = project.getFeature('tbl');
    const logEnabled = project.getSettingValue('log', 'ENABLED');
    const TABLES_ENABLED = DATA_TABLES && DATA_TABLES.getEnabled();
    const COMMANDSFeature = project.getFeature('commands');
    const appVersion = project.getSettingValue('general', 'FIRMWAREVERSION');
    const TLS_ENABLED = feature.getSettingValue('TLS', true) === '1';
    const TIMEZONE = project.getSettingValue('general', 'TIMEZONE');
    const LOGGING = project.getSettingValue('general', 'LOGGING');

    if (TLS_ENABLED) {
        SERVER_PORT = 8883;
    }
    let commands = [];
    if (COMMANDSFeature) {
        commands = COMMANDSFeature.getSettingValue('COMMANDS');
    }

    let variablesCode = '';
    let variableSetCode = '';
    // let tablesSendCode = '';
    // let commandsCode = '';
    // let variableGetCode = '';
    const variablesGetCode = [];
    // let cmdExecCode = '';
    const variables = project.getVariables();
    const widgets = feature.getSettingValue('WIDGETS');
    const widgetsArray = [];
    blocks.push({
        location: 'CGG_SETUP_TITLE',
        blockType: BlockType.LOCATION,
        template: `
        "title": "Device Console"`,
    });

    for (let i = 0; i < TABLES.length; i++) {
        const tblItem = project.getFeature(TABLES[i].getSettingValue('TABLE')) as TableItem;
        if (!tblItem) {
            continue;
        }
        const tableJson = Tbl.getTableJson(tblItem);
        blocks.push({
            location: 'CGG_CONSOLE_TABLES',
            blockType: BlockType.CODE,
            template: `
            ${JSON.stringify(tableJson)},`,
        });
    }

    const variableUpdateCodes: { [name: string]: string } = {};
    const enabledVariables = feature.getVariables();
    for (let i = 0; i < variables.length; i++) {
        const variable = variables[i];
        const variableName = variable.getSettingValue('VARNAME');
        if (!enabledVariables.includes(variableName)) {
            continue;
        }
        let variableDatatype = variable.getSettingValue('DATATYPE');
        if (variableDatatype === TibboDataType.DATETIME) {
            variableDatatype = TibboDataType.DWORD;
        }
        if (variableDatatype === TibboDataType.TIME) {
            variableDatatype = TibboDataType.FLOAT;
        }
        // const variableValue = project.convertDataType(
        //     variableName, variableDatatype, TibboDataType.STRING,
        // );
        const variableValue = variableName;
        let variableString = `"+${variableValue}+"`;
        if (variableDatatype === TibboDataType.STRING) {
            variableString = `\\x22"+${variableValue}+"\\x22`;
        }
        const variableUpdateCode = `cggInstance.publish_variable("${variableName}", ${variableValue})`;
        variablesCode += variableUpdateCode;
        variableUpdateCodes[variableName] = variableUpdateCode;

        blocks.push({
            location: `var_${variableName}_changed`,
            blockType: BlockType.CODE,
            template: `
if cgg_connected:
    ${variableUpdateCode}
            `,
        });

        blocks.push({
            location: 'CGG_PROC_TIMER',
            blockType: BlockType.CODE,
            template: `${variableUpdateCode}`,
        });

        let convertedValue = project.convertDataType('value', TibboDataType.STRING, variableDatatype);
        if (variableDatatype === TibboDataType.FLOAT) {
            convertedValue = 'int(value) if float(value) % 10 == 0 else float(value)';
        }

        variableSetCode += `
        if variable_name == "${variableName}":
            value=${project.convertDataType('value', TibboDataType.STRING, variableDatatype)}
            var_${variableName}_set(value)
            ${variableUpdateCode}`;
    }

    let commandsCode = '';
    for (let i = 0; i < commands.length; i++) {
        const command = commands[i];
        const commandName = command.getSettingValue('NAME');
        commandsCode += `
            if cmd == "${commandName}":
                cmd_${commandName}(cmd_data)`;
    }

    if (widgets.length > 0) {
        for (let i = 0; i < widgets.length; i++) {
            const widget = widgets[i].getWidgetObject();
            let { variable, linkType } = widget;
            try {
                const item = project.getFeature(variable);
                if (item?.id === 'variable') {
                    variable = item.getSettingValue('VARNAME');
                    linkType = 'variable';
                } else if (item?.id === 'command') {
                    variable = item.getSettingValue('NAME');
                    linkType = 'command';
                }
                widget.variable = variable;
                widget.linkType = linkType;
                widgetsArray.push(widget);
            } catch (ex) {
                widgets.splice(i, 1);
                i--;
            }
        }
    }

    blocks.push({
        location: 'CGG_CONSOLE_SETUP_WIDGETS',
        blockType: BlockType.CODE,
        template: `
        "updateInterval": ${feature.getSettingValue('INTERVAL') * 1000},
        "widgets": ${JSON.stringify(widgetsArray)},`,
    });

    blocks.push({
        location: 'CGG_APP_VERSION',
        blockType: BlockType.CODE,
        template: `
        "appName": "${project.name}",
        "appVersion": "${appVersion}",
        "appId": "${project.id}",
        `,
    });

    blocks.push({
        location: 'callback_mqtt_connect_ok',
        blockType: BlockType.CODE,
        template: `
global cgg_connected
cggInstance.publish(f'devices/{${USERNAME}}/state/timezone', ${TIMEZONE})
cgg_connected=True
`,
    });

    blocks.push({
        location: 'IMPORTS',
        blockType: BlockType.CODE,
        template: `
from cgg import cggHandler
`,
    });


    blocks.push({
        location: 'VARIABLE_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
cggInstance=None
cgg_connected=False
cgg_vars_sent=False
`,
    });

    blocks.push({
        location: project.getNetworkConnectedLocation('current_interface'),
        blockType: BlockType.CODE,
        template: `
global cggInstance
cggInstance=cggHandler(${SERVER}, ${SERVER_PORT}, ${USERNAME}, ${PASSWORD}, ${TLS_ENABLED ? 'True' : 'False'}, callback_mqtt_notif, callback_mqtt_connect_ok)
cggInstance.connect()
`,
    });

    blocks.push({
        location: 'FUNCTION_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
def callback_mqtt_notif(topic, msg):
    parts = topic.split('/')
    if len(parts) < 3:
        return
    if parts[2] == 'state' and parts[3] == 'variables':
        variable_name = parts[4]
        message = json.loads(msg)
        value = message['data']
        ${variableSetCode}

        pass
    elif parts[2] == 'state' and parts[3] == 'tables':
        
        pass
    elif parts[2] == 'commands':
        if parts[3] == 'I':
            stg_restore_multiple()
            global cgg_vars_sent
            cgg_vars_sent=False
        elif parts[3] == 'E':
            machine.reset()
        else:
            cmd = parts[3]
            message = json.loads(msg)
            cmd_data = message['data']
            ${commandsCode}
    elif parts[2] == 'firmware':

        pass
    elif parts[2] == 'system':

        pass
`,
    });


    let settingsCode = '';
    for (let i = 0; i < GROUPS.length; i++) {
        const tmpBlocks = GROUPS[i].getCodeBlocks();
        for (let j = 0; j < tmpBlocks.length; j++) {
            if (tmpBlocks[j].location === 'LUIS_GROUP_ITEM') {
                settingsCode += tmpBlocks[j].template;
                if (i + 1 < GROUPS.length) {
                    settingsCode += ',';
                }
            }
        }
    }

    blocks.push({
        location: 'CGG_SETTINGS_SCHEMA',
        template: settingsCode,
    });

    blocks.push({
        location: 'loop',
        blockType: BlockType.CODE,
        template: `
global cgg_vars_sent
if cgg_vars_sent==False and cgg_connected==True:
    #****CGG_PROC_TIMER****#
    cgg_vars_sent=True`,
    });

    if (LOGGING === '"tbl_log"') {
        blocks.push({
            location: 'print_overrides',
            blockType: BlockType.LOCATION,
            template: `
if cgg_connected:
    log_str = " ".join(map(str, args))
    curr_dt = datetime_current_timestamp()
    payload={
        'createdAt': curr_dt,
        'data': log_str,
        'type': 'debug',
    }
    cggInstance.publish(f'devices/{${USERNAME}}/logs', json.dumps(payload))`,
        });
    }

    return blocks;
};
